import React, { useEffect, useMemo, useRef, useState } from 'react'
import { runUname, getNeofetch } from './systemInfo'
import { terminalConfig } from '../config/terminal.config'

export type TerminalLine =
	| { type: 'system'; text: string; color?: string }
	| { type: 'command'; text: string }
	| { type: 'output'; text: string; color?: string }
	| { type: 'error'; text: string; color?: string }
	| { type: 'empty' }

// 启动时的"欢迎 + 系统信息"输出
// 每次组件挂载都重新生成（时间、屏幕尺寸等实时）
const buildBootLines = (): TerminalLine[] => {
	const lines: TerminalLine[] = [
		{
			type: 'system',
			text: `${terminalConfig.brand.appName} Terminal ${terminalConfig.version.app.match(/v[\d.]+/)?.[0] ?? ''}`
		},
		{ type: 'system', text: 'last login: ' + new Date().toLocaleString('zh-CN', { hour12: false }) },
		{ type: 'empty' },
		// 启动时自动跑一次 neofetch（展示系统信息，类似 Linux shell login 后行为）
		{ type: 'command', text: 'neofetch' },
		...getNeofetch(),
		{ type: 'empty' },
		{ type: 'system', text: 'Type "help" for available commands.' },
		{ type: 'empty' }
	]
	return lines
}

// 命令执行：返回输出行（不含用户输入的命令本身）
const executeCommand = (raw: string): TerminalLine[] | 'clear' => {
	const cmd = raw.trim()
	if (!cmd) return []

	const [name, ...args] = cmd.split(/\s+/)
	const arg = args.join(' ')

	switch (name) {
		case 'help':
			return [
				{ type: 'output', text: 'Available commands:' },
				{ type: 'output', text: '  help              Show this help message' },
				{ type: 'output', text: '  version           Show application version' },
				{ type: 'output', text: '  whoami            Show current user' },
				{ type: 'output', text: '  about             About this application' },
				{ type: 'output', text: '  date              Show current date and time' },
				{ type: 'output', text: '  uname [-a]        System info (POSIX-style)' },
				{ type: 'output', text: '  neofetch          System info with ASCII art' },
				{ type: 'output', text: '  ls                List files in current directory' },
				{ type: 'output', text: '  pwd               Show current directory' },
				{ type: 'output', text: '  echo <text>       Print text' },
				{ type: 'output', text: '  clear             Clear the terminal' }
			]
		case 'version':
		case 'v':
			return [{ type: 'output', text: terminalConfig.version.app }]
		case 'whoami':
			return [{ type: 'output', text: `${terminalConfig.brand.user}@${terminalConfig.brand.hostname}` }]
		case 'about':
			return [
				{ type: 'output', text: `${terminalConfig.brand.appName} — 基于 Semi Design 的现代化后台模板` },
				{ type: 'output', text: 'fork 自 semi-design-pro，"+1" 理念：在现有基础上多做一点' },
				{ type: 'output', text: 'GitHub: github.com/ltst-slk/plus-one-ui' }
			]
		case 'date':
			return [{ type: 'output', text: new Date().toLocaleString('zh-CN', { hour12: false }) }]
		case 'uname':
			return runUname(args)
		case 'neofetch':
			return getNeofetch()
		case 'ls':
			return [{ type: 'output', text: 'about.txt  config/  logs/  src/' }]
		case 'pwd':
			return [{ type: 'output', text: `/home/${terminalConfig.brand.user}/${terminalConfig.brand.hostname}` }]
		case 'echo':
			return [{ type: 'output', text: arg }]
		case 'clear':
		case 'cls':
			return 'clear'
		default:
			return [{ type: 'error', text: `command not found: ${name}. Type 'help' for available commands.` }]
	}
}

const Terminal: React.FC = () => {
	// useMemo([]) 让 boot 信息只在首次挂载生成
	const initialLines = useMemo(() => buildBootLines(), [])
	const [history, setHistory] = useState<TerminalLine[]>(initialLines)
	const [input, setInput] = useState('')
	const [historyIdx, setHistoryIdx] = useState(-1)
	const cmdHistoryRef = useRef<string[]>([])
	const inputRef = useRef<HTMLInputElement>(null)
	const bodyRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (bodyRef.current) {
			bodyRef.current.scrollTop = bodyRef.current.scrollHeight
		}
	}, [history])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		const cmd = input
		const result = executeCommand(cmd)

		if (result === 'clear') {
			setHistory(BOOT_LINES_MIN)
		} else if (cmd.trim()) {
			cmdHistoryRef.current = [...cmdHistoryRef.current, cmd]
			setHistory([...history, { type: 'command', text: cmd }, ...result, { type: 'empty' }])
		} else {
			setHistory([...history, { type: 'command', text: '' }, { type: 'empty' }])
		}
		setInput('')
		setHistoryIdx(-1)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowUp') {
			e.preventDefault()
			const cmds = cmdHistoryRef.current
			if (cmds.length === 0) return
			const next = historyIdx < 0 ? cmds.length - 1 : Math.max(0, historyIdx - 1)
			setInput(cmds[next])
			setHistoryIdx(next)
		} else if (e.key === 'ArrowDown') {
			e.preventDefault()
			const cmds = cmdHistoryRef.current
			if (historyIdx < 0) return
			const next = historyIdx + 1
			if (next >= cmds.length) {
				setInput('')
				setHistoryIdx(-1)
			} else {
				setInput(cmds[next])
				setHistoryIdx(next)
			}
		} else if (e.key === 'l' && e.ctrlKey) {
			e.preventDefault()
			setHistory(BOOT_LINES_MIN)
		}
	}

	const handleContainerClick = () => {
		inputRef.current?.focus()
	}

	return (
		<div className="login-sider-terminal" onClick={handleContainerClick}>
			<div className="login-terminal-titlebar">
				<div className="login-terminal-traffic-lights" aria-hidden>
					<span className="close" />
					<span className="minimize" />
					<span className="maximize" />
				</div>
				<div className="login-terminal-titlebar-text">
					{terminalConfig.brand.user}@{terminalConfig.brand.hostname}: ~
				</div>
			</div>

			<div className="login-terminal-body" ref={bodyRef}>
				{history.map((line, i) => (
					<div
						key={i}
						className={`terminal-line terminal-line-${line.type}${
							line.type === 'system' && line.color === '#ffffff' ? ' terminal-line-logo' : ''
						}`}
					>
						{line.type === 'command' && (
							<>
								<span className="prompt">$</span>
								<span className="command-text">{line.text}</span>
							</>
						)}
						{line.type !== 'command' && line.type !== 'empty' && (
							<span
								className={`line-text${line.color && (line.color === 'primary' || line.color === 'accent') ? ` line-${line.color}` : ''}`}
								style={
									line.color && line.color !== 'primary' && line.color !== 'accent' ? { color: line.color } : undefined
								}
							>
								{line.text}
							</span>
						)}
					</div>
				))}

				<form onSubmit={handleSubmit} className="terminal-input-line">
					<span className="prompt">$</span>
					<input
						ref={inputRef}
						type="text"
						value={input}
						onChange={(e) => setInput(e.target.value)}
						onKeyDown={handleKeyDown}
						autoFocus
						spellCheck={false}
						autoComplete="off"
					/>
				</form>
			</div>
		</div>
	)
}

// clear 后保留一个最简 boot，不重复展示系统信息
const BOOT_LINES_MIN: TerminalLine[] = [
	{
		type: 'system',
		text: `${terminalConfig.brand.appName} Terminal ${terminalConfig.version.app.match(/v[\d.]+/)?.[0] ?? ''}`
	},
	{ type: 'empty' }
]

export default Terminal
